import { RootState } from '@/store/store'
import { createSelector } from '@reduxjs/toolkit'
import { selectAgentV2SendableChatFileIds, selectAgentV2SendableChatWritingSampleFileId } from '@/agent-v2/conversations/store/selectors'
import { selectLoadedUserFilesById, selectUnavailableUserFileIds } from '@/user_files/files/store/selectors'
import { getUserFileAttachmentStatus, type UserFileAttachmentStatus } from '@/chat-common/selected_files/utils/user-file-upload-status'
import { selectAllFiles, selectUploadFilesByServerFileId } from '@/uppy-file-upload/store/selectors'
import type { FileUploadState } from '@/uppy-file-upload/store/slice'
import type { PendingUpload } from './slice'
import dayjs from 'dayjs'

export const selectAgentConversationFormWebSearchEnabled = (state: RootState, props: { conversationId: string }) =>
  state.agentConversationFormState.forms[props.conversationId]?.webSearchEnabled ?? true

/**
 * Select Agent Conversation Form User File IDs
 * Get the list of selected user file ids for a conversation.
 */
export const selectAgentConversationFormUserFileIds = createSelector(
  (state: RootState, props: { conversationId: string }) => state.agentConversationFormState.forms[props.conversationId],

  (form) => form?.requestParams.user_files_rag?.user_file_ids ?? []
)

/**
 * Select Agent Conversation Form Writing Sample File Ids
 * Get the user_file ids selected as writing samples for a conversation.
 *
 * @returns The list of writing-sample user_file ids
 */
export const selectAgentConversationFormWritingSampleFileIds = createSelector(
  (state: RootState, props: { conversationId: string }) => state.agentConversationFormState.forms[props.conversationId],

  (form) => form?.requestParams.writing_sample_files?.user_file_ids ?? []
)

/** The composer's files that can still be sent. A deleted file would fail the whole turn, so it's left out. */
const selectAgentConversationFormSendableUserFileIds = createSelector(
  [selectAgentConversationFormUserFileIds, selectUnavailableUserFileIds],
  (userFileIds, unavailableFileIds) => userFileIds.filter((fileId) => !unavailableFileIds[fileId])
)

/** The composer's writing sample, unless it has been deleted. */
const selectAgentConversationFormSendableWritingSampleFileId = (state: RootState, props: { conversationId: string }) =>
  selectAgentConversationFormWritingSampleFileIds(state, props).find((fileId) => !selectUnavailableUserFileIds(state)[fileId]) ?? null

/** Whether the next turn sends the composer's writing sample in place of the chat's. */
export const selectAgentV2IsChatWritingSampleReplaced = (state: RootState, props: { conversationId: string }) =>
  selectAgentConversationFormSendableWritingSampleFileId(state, props) !== null

/** Whether a chat file the next turn sends is still processing, failed, or hasn't loaded. */
const selectAgentV2HasUnreadyChatFiles = createSelector(
  [selectAgentV2SendableChatFileIds, selectAgentV2SendableChatWritingSampleFileId, selectAgentV2IsChatWritingSampleReplaced, selectLoadedUserFilesById],
  (chatFileIds, writingSampleFileId, isWritingSampleReplaced, loadedUserFilesById) =>
    [...chatFileIds, ...(writingSampleFileId && !isWritingSampleReplaced ? [writingSampleFileId] : [])].some(
      (fileId) => getUserFileAttachmentStatus({ file: loadedUserFilesById[fileId] }).status !== 'ready'
    )
)

/** The files the next turn sends: the chat's files plus the composer's. */
export const selectAgentV2NextTurnFileIds = createSelector(
  [selectAgentV2SendableChatFileIds, selectAgentConversationFormSendableUserFileIds],
  (chatFileIds, composerFileIds) => Array.from(new Set([...chatFileIds, ...composerFileIds]))
)

/** The writing sample the next turn sends: the composer's when one is picked, otherwise the chat's. */
export const selectAgentV2NextTurnWritingSampleFileId = (state: RootState, props: { conversationId: string }) =>
  selectAgentConversationFormSendableWritingSampleFileId(state, props) ?? selectAgentV2SendableChatWritingSampleFileId(state, props)

/** The unsent text in a conversation's composer. */
export const selectAgentConversationFormInputText = (state: RootState, props: { conversationId: string }) =>
  state.agentConversationFormState.inputTextByConversationId[props.conversationId] ?? ''

const selectAgentConversationFormPendingUploads = createSelector(
  (state: RootState, props: { conversationId: string }) => state.agentConversationFormState.pendingUploadsByConversationId[props.conversationId],

  (pendingUploads): PendingUpload[] => pendingUploads ?? []
)

export const selectAgentConversationFormAttachedPendingUploads = createSelector(
  (state: RootState, props: { conversationId: string }) => state.agentConversationFormState.attachedPendingUploadsByConversationId[props.conversationId],

  (pendingUploads): PendingUpload[] => pendingUploads ?? []
)

// A pending upload joined to its live upload state (target rides along so the chip can render in the right slot).
export type ResolvedPendingUpload = PendingUpload & { uploadState?: FileUploadState }

export const selectAgentConversationFormPendingUserFileUploads = createSelector(
  [selectAgentConversationFormPendingUploads, selectAllFiles],
  (pendingUploads, uploadFilesByClientTmpId): ResolvedPendingUpload[] =>
    pendingUploads.map((upload) => ({
      ...upload,
      uploadState: uploadFilesByClientTmpId[upload.clientTmpId],
    }))
)

const selectAgentConversationFormAttachedPendingUserFileUploads = createSelector(
  [selectAgentConversationFormAttachedPendingUploads, selectAllFiles],
  (pendingUploads, uploadFilesByClientTmpId): ResolvedPendingUpload[] =>
    pendingUploads.map((upload) => ({
      ...upload,
      uploadState: uploadFilesByClientTmpId[upload.clientTmpId],
    }))
)

const selectAgentConversationFormUserFileAttachments = createSelector(
  [
    selectAgentConversationFormUserFileIds,
    selectAgentConversationFormWritingSampleFileIds,
    selectUploadFilesByServerFileId,
    selectLoadedUserFilesById,
    selectUnavailableUserFileIds,
  ],
  // Writing samples are user_files too, so include them in readiness/processing/error checks —
  // an unready writing sample should block submit and show the processing/error state, just like
  // a RAG user-file selection. A deleted file is left out of the turn, so it doesn't hold up submit.
  (userFileIds, writingSampleFileIds, uploadFilesByServerFileId, loadedUserFilesById, unavailableFileIds) =>
    Array.from(new Set([...userFileIds, ...writingSampleFileIds]))
      .filter((fileId) => !unavailableFileIds[fileId])
      .map((fileId) => {
        const uploadState = uploadFilesByServerFileId[fileId]
        return {
          fileId,
          uploadState,
          file: uploadState?.backend?.record ?? loadedUserFilesById[fileId],
        }
      })
)

const selectAgentConversationFormAttachedUserFileAttachmentStatuses = createSelector(
  [selectAgentConversationFormUserFileAttachments, selectAgentConversationFormAttachedPendingUserFileUploads],
  (attachments, pendingUploads): UserFileAttachmentStatus[] => [
    ...attachments.map(({ file, uploadState }) => getUserFileAttachmentStatus({ file, uploadState }).status),
    ...pendingUploads.map(({ uploadState }) => getUserFileAttachmentStatus({ uploadState }).status),
  ]
)

export const selectAgentConversationFormHasUnreadyUserFiles = createSelector(
  [selectAgentConversationFormAttachedUserFileAttachmentStatuses],
  (statuses): boolean => statuses.some((status) => status !== 'ready')
)

/** Whether any file the next turn sends, from the composer or the chat, isn't ready to send yet. */
export const selectAgentV2NextTurnHasUnreadyFiles = (state: RootState, props: { conversationId: string }) =>
  selectAgentConversationFormHasUnreadyUserFiles(state, props) || selectAgentV2HasUnreadyChatFiles(state, props)

/**
 * Select Agent Conversation Form Caselaw Search Filter
 * Get the caselaw search filter for a conversation
 *
 * @returns The caselaw search filter or null if it is not set
 */
export const selectAgentConversationFormCaselawSearchFilter = createSelector(
  (state: RootState, props: { conversationId: string }) => state.agentConversationFormState.forms[props.conversationId],
  // Make the check for the jurisdiction codes to be not empty
  (form) => {
    const filter = form?.requestParams?.caselaw_search_filter
    if (filter && Array.isArray(filter.jurisdiction_codes) && filter.jurisdiction_codes.length > 0) {
      return filter
    }
    return null
  }
)

/**
 * Select Agent Conversation Form Caselaw Date Range
 * Get the caselaw date range for a conversation
 *
 * @returns Object with dateLow and dateHigh in YYYY-MM format, or { dateLow: null, dateHigh: null } if not set
 */
export const selectAgentConversationFormCaselawDateRange = createSelector(
  (state: RootState, props: { conversationId: string }) => state.agentConversationFormState.forms[props.conversationId],
  (form) => {
    const filter = form?.requestParams?.caselaw_search_filter
    return {
      dateLow: filter?.date_low ?? null,
      dateHigh: filter?.date_high ?? null,
    }
  }
)

/**
 * Select Agent Conversation Form LRR Search Filter
 * Get the lrr search filter for a conversation
 *
 * @returns The lrr search filter or null if it is not set
 */
export const selectAgentConversationFormLRRSearchFilter = createSelector(
  (state: RootState, props: { conversationId: string }) => state.agentConversationFormState.forms[props.conversationId],
  (form) => {
    const filter = form?.requestParams?.lrr_search_filter
    if (filter && Array.isArray(filter.jurisdiction_codes) && filter.jurisdiction_codes.length > 0) {
      return filter
    }
    return null
  }
)

/**
 * Select whether a conversation already has jurisdiction filter params.
 *
 * Explicit empty filters count as present so Clear All does not fall back to matter/user defaults.
 */
export const selectAgentConversationFormHasJurisdictionFilters = createSelector(
  (state: RootState, props: { conversationId: string }) => state.agentConversationFormState.forms[props.conversationId],
  (form) => {
    return Boolean(form?.requestParams.lrr_search_filter || form?.requestParams.caselaw_search_filter)
  }
)

export const selectDefaultResearchJurisdictionForConversation = createSelector(
  (state: RootState, props: { conversationId: string }) => state.agentV2ConversationsState.conversations[props.conversationId]?.matter_id ?? null,
  (state: RootState) => state.mattersState.matters,
  (matterId, matters) => {
    if (!matterId) return null

    const matter = matters[matterId]
    if (!matter) return null

    return matter.default_jurisdiction
  }
)

/**
 * Select Agent Conversation Form Research Enabled
 * Get values from the caselaw and lrr search filters and check if research is enabled
 *
 * Research is enabled when caselaw search filter or lrr search filter has jurisdiction codes
 *
 * @returns true if research is enabled, false otherwise
 */
export const selectAgentConversationFormResearchEnabled = createSelector(
  (state: RootState, props: { conversationId: string }) => selectAgentConversationFormCaselawSearchFilter(state, props),
  (state: RootState, props: { conversationId: string }) => selectAgentConversationFormLRRSearchFilter(state, props),
  (state: RootState, props: { conversationId: string }) => selectAgentConversationFormCaselawDateRangeValid(state, props),
  (caselawSearchFilter, lrrSearchFilter, dateRangeValid) => {
    return dateRangeValid && ((caselawSearchFilter?.jurisdiction_codes?.length ?? 0) > 0 || (lrrSearchFilter?.jurisdiction_codes?.length ?? 0) > 0)
  }
)

/**
 * Select Agent Conversation Form Caselaw Date Range Is Valid
 * Check if the caselaw date range is valid (dateLow is before dateHigh)
 *
 * @returns true if the date range is valid, false otherwise
 */
export const selectAgentConversationFormCaselawDateRangeValid = createSelector(
  (state: RootState, props: { conversationId: string }) => selectAgentConversationFormCaselawDateRange(state, props),
  (dateRange) => {
    // If both are null, range is valid (no filtering)
    if (!dateRange?.dateLow && !dateRange?.dateHigh) {
      return true
    }
    // If either is set but invalid, range is invalid
    if ((dateRange?.dateLow && !dayjs(dateRange?.dateLow).isValid()) || (dateRange?.dateHigh && !dayjs(dateRange?.dateHigh).isValid())) {
      return false
    }
    // Check if dateLow is before dateHigh
    return !dateRange?.dateLow || !dateRange?.dateHigh || dayjs(dateRange?.dateLow).isBefore(dayjs(dateRange?.dateHigh))
  }
)

/**
 * Select if include unpublished cases are included
 *
 * @returns true if unpublished cases are included, false otherwise
 */
export const selectAgentConversationFormIncludeUnpublishedCases = createSelector(
  (state: RootState, props: { conversationId: string }) => state.agentConversationFormState.forms[props.conversationId],
  (form) => form?.requestParams.caselaw_search_filter?.include_unpublished_cases ?? false
)
